import { Box, Text, Checkbox, Tr, Flex } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../../../../../context/ApiProvider';
import { ParentInterface } from '../../../../../shared/type/parent.type';
import CustomChakraButton from '../../../../CustomChakraButton/CustomChakraButton';
import AddParent from './AddParent/AddParent';
import ImportParentsFromFilePopUp from './ImportParentsFromFilePopUp/ImportParentsFromFilePopUp';
import CustomAlert from '../../../../CustomAlert/CustomAlert';
import IconComponent from '../../../../IconComponent/IconComponent';
import CreateNewAdminThread from '../../../MailBox/CreateNewAdminThread/CreateNewAdminThread';
import { StickyScrollableTableContainer } from '../../../../DataTable/StickyScrollableTableContainer';
import useAuth from '../../../../../hooks/useAuth';

const ParentsTable = () => {
  const location = useLocation();
  const columnHelper = createColumnHelper<ParentInterface>();
  const { apiTenantParentController } = useApi();
  const { id: tenantId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();

  const [parents, setParents] = useState<ParentInterface[]>([]);
  const [visibleParents, setVisibleParents] = useState<ParentInterface[]>([]);

  const [selectedParents, setSelectedParents] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPopUp, setShowPopUp] = useState({
    showImportParentsFromFilePopUp: false,
    showAddParentPopUp: false,
    deleteParents: false
  });
  const [sendMessagePopUp, setSendMessagePopUp] = useState({ show: false, userId: 0 });

  const handleShowPopUpValue = (name: string, value: boolean) => {
    setShowPopUp((prev) => ({ ...prev, [name]: value }));
  };

  const fetchParents = async () => {
    setIsLoading(true);
    try {
      const response = await apiTenantParentController('parent-list').post(`/${tenantId}`, {});
      setParents(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteParents = useCallback(
    (parentsToDelete: number[]) => {
      Promise.all(
        parentsToDelete.map(async (parentId) => {
          try {
            const response = await apiTenantParentController('').delete(`/${tenantId}/${parentId}`);
            if (response.status === 204) return parentId;
          } catch (error) {
            console.error(`Błąd usuwania rodzica ${parentId}:`, error);
          }
        })
      ).then((deletedIds) => {
        const successfullyDeleted = deletedIds.filter(Boolean);
        setSelectedParents((prev) => prev.filter((id) => !successfullyDeleted.includes(id)));
        fetchParents();
      });
    },
    [tenantId]
  );

  const columns = [
    columnHelper.display({
      id: 'select',
      header: () => {
        const isEmpty = parents.length === 0;
        const isAllSelected = !isEmpty && selectedParents.length === visibleParents.length;
        return (
          <Checkbox
            onChange={() => {
              setSelectedParents(isAllSelected ? [] : visibleParents.map((p) => p.id));
            }}
            isChecked={isAllSelected}
            isDisabled={isEmpty}
            colorScheme="customOrange"
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          isChecked={selectedParents.includes(row.original.id)}
          onChange={() => {
            const id = row.original.id;
            setSelectedParents((prev) =>
              prev.includes(id) ? prev.filter((el) => el !== id) : [...prev, id]
            );
          }}
          colorScheme="customOrange"
        />
      ),
      size: 40
    }),
    columnHelper.display({
      id: 'rowNumber',
      cell: (info) => <span>{info.row.index + 1}.</span>,
      header: 'Lp.',
      size: 40,
      meta: { isNumeric: true }
    }),
    columnHelper.accessor('name', {
      header: 'Nazwisko imię',
      cell: ({ row }) => {
        const path = location.pathname.replace(
          'parents-table',
          `single-parent/${row.original.id}/list-of-orders`
        );
        return (
          <p className="text-sm font-medium text-grayLight-900 underline underline-offset-4">
            <Link to={path}>{row.original.name}</Link>
          </p>
        );
      },
      size: 180
    }),
    columnHelper.display({
      id: 'message',
      header: '',
      cell: ({ row }) => (
        <button
          onClick={() => setSendMessagePopUp({ show: true, userId: row.original.id })}
          className="m-auto">
          <IconComponent iconName="message" color="#475467" />
        </button>
      ),
      size: 40
    }),
    columnHelper.accessor('login', {
      header: 'Adres email / login',
      cell: (info) => <p className="text-sm font-medium text-grayLight-900">{info.getValue()}</p>,
      size: 180
    }),
    columnHelper.accessor('phone', {
      header: 'Telefon',
      cell: (info) => <p className="text-sm text-grayLight-900">{info.getValue() || '-'}</p>,
      size: 140
    }),
    columnHelper.accessor('children', {
      header: 'Przypisani uczniowie',
      cell: ({ row }) => {
        const path = (id: number) =>
          location.pathname.replace('parents/parents-table', `children/single-kid/${id}/orders`);

        return (
          <ul className="flex flex-col">
            {row.original.children.map((kid) => (
              <li
                key={kid.id}
                className="text-sm font-medium text-grayLight-900 underline underline-offset-4">
                <Link to={path(kid.id)}>
                  {kid.lastName} {kid.firstName}{' '}
                  {kid.organisationUnitName && `(${kid.organisationUnitName})`}
                </Link>
              </li>
            ))}
          </ul>
        );
      },
      size: 220
    }),
    columnHelper.accessor('bankAccountPresent', {
      header: 'Numer konta',
      cell: (info) => (
        <p className="text-sm text-grayLight-900">{info.getValue() ? 'tak' : 'nie'}</p>
      ),
      size: 100
    }),
    columnHelper.accessor('creationDate', {
      header: 'Data stworzenia',
      cell: (info) => <p className="text-sm text-grayLight-900">{info.getValue()}</p>,
      size: 120
    })
  ];

  useEffect(() => {
    if (tenantId !== 'undefined') fetchParents();
  }, [tenantId]);

  return (
    <section className="ParentsTable p-spacing-xl w-full pb-28">
      <Box mb={6} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <Text fontSize="xl" fontWeight="semibold" color="gray.900">
            Rodzice
          </Text>
          <Text fontSize="sm" color="gray.600">
            Lista rodziców przypisanych do oddziału
          </Text>
        </div>
        <div className="flex gap-2 flex-wrap">
          <CustomChakraButton
            size="md"
            hierarchy="linkGray"
            iconPosition="left"
            icon="import"
            buttonProps={{
              onClick: () => handleShowPopUpValue('showImportParentsFromFilePopUp', true)
            }}>
            Importuj listę
          </CustomChakraButton>
          <CustomChakraButton
            size="md"
            hierarchy="linkGray"
            iconPosition="left"
            icon="clock"
            buttonProps={{
              onClick: () => {
                if (auth.loggedUserDto.superAdmin) {
                  navigate(`/superAdmin/branches/id/${tenantId}/users/parents/archive-parents`);
                } else {
                  navigate(`/tenantAdmin/id/${tenantId}/users/parents/archive-parents`);
                }
              }
            }}>
            Archiwum
          </CustomChakraButton>
          <CustomChakraButton
            size="md"
            hierarchy="primary"
            iconPosition="left"
            icon="plus"
            buttonProps={{ onClick: () => handleShowPopUpValue('showAddParentPopUp', true) }}>
            Dodaj rodzica
          </CustomChakraButton>
        </div>
      </Box>

      <StickyScrollableTableContainer
        searchStorageKey="users-parents-search"
        sortingStorageKey="users-parents-sorting"
        data={parents}
        columns={columns}
        isLoading={isLoading}
        enableSearch={true}
        searchPlaceholder="Szukaj rodzica..."
        onVisibleRowsChange={(rows) => setVisibleParents(rows)}
        filtersRightContent={
          <CustomChakraButton
            size="sm"
            hierarchy="tertiaryColor"
            iconPosition="onlyText"
            buttonProps={{
              disabled: selectedParents.length === 0,
              onClick: () => handleShowPopUpValue('deleteParents', true)
            }}>
            Usuń zaznaczone
          </CustomChakraButton>
        }
      />

      {showPopUp.showImportParentsFromFilePopUp && (
        <ImportParentsFromFilePopUp
          isOpen
          onClose={() => {
            handleShowPopUpValue('showImportParentsFromFilePopUp', false);
            fetchParents();
          }}
        />
      )}

      {showPopUp.showAddParentPopUp && (
        <AddParent
          isOpen
          onClose={() => {
            handleShowPopUpValue('showAddParentPopUp', false);
            fetchParents();
          }}
        />
      )}

      {sendMessagePopUp.show && (
        <CreateNewAdminThread
          isOpen
          onClose={() => setSendMessagePopUp({ show: false, userId: 0 })}
          initialTargetUserId={sendMessagePopUp.userId}
        />
      )}

      <CustomAlert
        handleOpen={showPopUp.deleteParents}
        icon={
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-error-100">
            <IconComponent iconName="trash" />
          </div>
        }
        header="Usunąć zaznaczonych rodziców?"
        content={
          <div className="flex flex-col gap-spacing-xl">
            <p className="font-normal text-sm">
              Usunięcie rodziców jest nieodwracalne i wiąże się z utratą danych.
            </p>
            <ul className="list-inside text-sm text-grayLight-900">
              {parents
                .filter((parent) => selectedParents.includes(parent.id))
                .map((parent) => (
                  <li key={parent.id}>
                    <Checkbox
                      isChecked={selectedParents.includes(parent.id)}
                      onChange={() => {
                        setSelectedParents((prev) =>
                          prev.includes(parent.id)
                            ? prev.filter((id) => id !== parent.id)
                            : [...prev, parent.id]
                        );
                      }}
                      colorScheme="customOrange">
                      {parent.name}
                    </Checkbox>
                  </li>
                ))}
            </ul>
          </div>
        }
        cancelButtonText="Anuluj"
        cancelButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'secondaryGray' }}
        confirmButtonText="Usuń"
        confirmButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'primary' }}
        onCancelAction={() => handleShowPopUpValue('deleteParents', false)}
        onConfirmAction={() => {
          deleteParents(selectedParents);
          handleShowPopUpValue('deleteParents', false);
        }}
      />
    </section>
  );
};

export default ParentsTable;
